/* Hallmark · macrostructure: Long Document · tone: calm-operational · anchor hue: heritage green
 * pre-emit critique: P5 H5 E5 S5 R5 V4 · contrast: pass · nav: none · footer: none
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Fingerprint,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  UserRound,
  UsersRound,
} from 'lucide-react';
import {
  BID_SUPPORT_WHATSAPP_URL,
  calculateAge,
  isValidPhone,
  PARTICIPANT_EDUCATION_LEVELS,
  PARTICIPANT_OCCUPATIONS,
} from '@lms/shared';
import { Button, Input } from '@lms/ui';
import type { Program, ProgramRegistration } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { CityAutocomplete } from '../components/CityAutocomplete';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useRegistrationProgram } from '../hooks/usePrograms';
import { apiBaseUrl, apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';

const publicRegistrationSchema = z
  .object({
    name: z.string().trim().min(2, 'fieldInvalid'),
    email: z.string().trim().email('fieldInvalid'),
    phone: z.string().trim().refine(isValidPhone, 'phoneInvalid'),
    dateOfBirth: z
      .string()
      .min(1, 'dateOfBirthRequired')
      .refine(value => calculateAge(value) !== null, 'dateOfBirthInvalid'),
    occupation: z.enum(PARTICIPANT_OCCUPATIONS, { message: 'fieldInvalid' }),
    occupationOther: z.string().trim().optional(),
    educationLevel: z.enum(PARTICIPANT_EDUCATION_LEVELS, { message: 'fieldInvalid' }),
    educationOther: z.string().trim().optional(),
    gender: z.enum(['MALE', 'FEMALE'], { message: 'fieldInvalid' }),
    password: z.string().min(8, 'passwordMin'),
    confirmPassword: z.string().min(8, 'confirmPasswordMin'),
    domisili: z.string().trim().min(2, 'fieldInvalid'),
    reason: z.string().trim().min(10, 'fieldInvalid'),
    consent: z.boolean().refine(value => value, { message: 'consentRequired' }),
  })
  .refine(values => values.password === values.confirmPassword, {
    message: 'passwordMismatch',
    path: ['confirmPassword'],
  })
  .refine(values => values.occupation !== 'OTHER' || (values.occupationOther?.length ?? 0) >= 2, {
    message: 'fieldInvalid',
    path: ['occupationOther'],
  })
  .refine(
    values => values.educationLevel !== 'OTHER' || (values.educationOther?.length ?? 0) >= 2,
    { message: 'fieldInvalid', path: ['educationOther'] }
  );

type PublicRegistrationInput = z.infer<typeof publicRegistrationSchema>;
type WhatsAppInvitation = NonNullable<
  NonNullable<ProgramRegistration['participantId']>['whatsappGroup']
>;

interface SubmissionState extends Pick<ProgramRegistration, 'id' | 'status'> {
  whatsappInvitation: WhatsAppInvitation | null;
  gender: PublicRegistrationInput['gender'];
  participantNip: string | null;
}

function isRegistrationAvailable(program: Pick<Program, 'isRegistrationOpen'>) {
  return program.isRegistrationOpen;
}

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message.trim()) return error.message;
  return null;
}

function getApiErrorStatus(error: unknown): number | null {
  if (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
  ) {
    return error.statusCode;
  }
  return null;
}

function isRetryableRegistrationConflict(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const status = getApiErrorStatus(error);
  if (status === 502 || status === 503 || status === 504) return true;
  if (status !== 409) return false;
  const message = getErrorMessage(error)?.toLowerCase() ?? '';
  return (
    message.includes('data serupa sudah tercatat') ||
    message.includes('sedang diproses') ||
    message.includes('pendaftaran belum dapat diselesaikan otomatis')
  );
}

export function PublicProgramRegistration() {
  const { code } = useParams<{ code: string }>();
  const { language, t } = useLanguage();
  const registrationReference = (code || '').trim();
  const {
    data: program,
    isLoading,
    isError,
    error,
    refetch,
  } = useRegistrationProgram(registrationReference);
  const [submission, setSubmission] = useState<SubmissionState | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const completionHeading = useRef<HTMLHeadingElement>(null);
  const accountCreatedFor = useRef<string | null>(null);
  const submissionInFlight = useRef(false);

  useEffect(() => {
    if (submission) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      completionHeading.current?.focus();
    }
  }, [submission]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PublicRegistrationInput>({
    resolver: zodResolver(publicRegistrationSchema),
    defaultValues: { consent: false, gender: undefined },
  });

  const domisili = watch('domisili') || '';
  const selectedGender = watch('gender');
  const dateOfBirth = watch('dateOfBirth') || '';
  const occupation = watch('occupation');
  const educationLevel = watch('educationLevel');
  const age = calculateAge(dateOfBirth);
  const password = watch('password') || '';
  const isAvailable = program ? isRegistrationAvailable(program) : false;
  const locale = language === 'ar' ? 'ar-SA' : 'id-ID';
  const isMissingRegistrationLink = getApiErrorStatus(error) === 404 || !registrationReference;

  const registrationWindow = useMemo(() => {
    if (!program) return t(copy.common.tbd);
    const formatDate = (date: Date | string | null | undefined) =>
      date
        ? new Date(date).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : t(copy.common.tbd);
    return `${formatDate(program.registrationOpensAt)} — ${formatDate(program.registrationClosesAt)}`;
  }, [locale, program, t]);

  const fieldError = (key: keyof typeof errors) => {
    const message = errors[key]?.message;
    if (message === 'passwordMin') return t(copy.publicRegistration.passwordMin);
    if (message === 'confirmPasswordMin') return t(copy.publicRegistration.confirmPasswordMin);
    if (message === 'passwordMismatch') return t(copy.publicRegistration.passwordMismatch);
    if (message === 'consentRequired') return t(copy.publicRegistration.consentRequired);
    if (message === 'phoneInvalid') return t(copy.publicRegistration.phoneInvalid);
    if (message === 'dateOfBirthRequired') return t(copy.publicRegistration.dateOfBirthRequired);
    if (message === 'dateOfBirthInvalid') return t(copy.publicRegistration.dateOfBirthInvalid);
    return message ? t(copy.publicRegistration.fieldInvalid) : undefined;
  };

  const submit = async (values: PublicRegistrationInput) => {
    if (!program || !isAvailable || submissionInFlight.current) return;

    submissionInFlight.current = true;
    setSubmitError(null);
    const normalizedEmail = values.email.trim().toLowerCase();
    try {
      try {
        if (accountCreatedFor.current !== normalizedEmail) {
          await apiClient.auth.register({
            email: normalizedEmail,
            password: values.password,
            name: values.name.trim(),
            phone: values.phone.trim(),
            gender: values.gender,
            dateOfBirth: values.dateOfBirth,
            occupation: values.occupation,
            occupationOther: values.occupation === 'OTHER' ? values.occupationOther : undefined,
            educationLevel: values.educationLevel,
            educationOther: values.educationLevel === 'OTHER' ? values.educationOther : undefined,
          });
          accountCreatedFor.current = normalizedEmail;
        }
      } catch (error) {
        const isExistingEmail =
          error instanceof Error &&
          /alamat email ini sudah terdaftar|email.*already|email.*exists/i.test(error.message);
        if (isExistingEmail) {
          // A first submission may have created the account successfully but
          // been interrupted before the program registration completed. The
          // public registration endpoint is idempotent for this email/program
          // pair, so continue it instead of sending the participant to login.
          // A phone collision remains a hard stop because it belongs to a
          // different account and is not matched by this email-specific guard.
        } else {
          throw error;
        }
      }

      const registrationInput = {
        programId: program.id,
        name: values.name.trim(),
        email: normalizedEmail,
        phone: values.phone.trim(),
        gender: values.gender,
        dateOfBirth: values.dateOfBirth,
        occupation: values.occupation,
        occupationOther: values.occupation === 'OTHER' ? values.occupationOther : undefined,
        educationLevel: values.educationLevel,
        educationOther: values.educationLevel === 'OTHER' ? values.educationOther : undefined,
        domisili: values.domisili.trim(),
        reason: values.reason.trim(),
        consentGiven: values.consent,
      };

      let registration: ProgramRegistration;
      try {
        registration = await apiClient.registrations.create(registrationInput);
      } catch (error: unknown) {
        // A lost response, gateway interruption or recoverable conflict can
        // occur after the server commits. Retry once so the idempotent API
        // returns the canonical registration without duplicating LMS data.
        if (!isRetryableRegistrationConflict(error)) throw error;
        await new Promise<void>(resolve => setTimeout(resolve, 350));
        registration = await apiClient.registrations.create(registrationInput);
      }

      setSubmission({
        id: registration.id,
        status: registration.status,
        gender: values.gender,
        // The API assigns the gender-appropriate WhatsApp group during
        // auto-approval. Keep this response data until the completion screen
        // renders; otherwise the invitation is silently discarded.
        whatsappInvitation: registration.participantId?.whatsappGroup ?? null,
        participantNip: registration.participantNip ?? null,
      });
    } catch (error) {
      setSubmitError(
        error instanceof TypeError
          ? t(copy.publicRegistration.networkError)
          : getErrorMessage(error) || t(copy.publicRegistration.unexpectedError)
      );
    } finally {
      submissionInFlight.current = false;
    }
  };

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6">
        <div
          className="flex flex-col items-center gap-3 text-on-surface-variant"
          aria-live="polite"
        >
          <Loader2 className="size-8 animate-spin text-primary" aria-hidden="true" />
          <Bilingual {...copy.registration.preparing} />
        </div>
      </main>
    );
  }

  if (isError || !program) {
    const isNotFound = isMissingRegistrationLink || !isError;
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <CircleAlert className="mx-auto size-10 text-error" aria-hidden="true" />
          <h1 className="mt-5 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface">
            <Bilingual
              {...(isNotFound
                ? copy.publicRegistration.invalidLinkTitle
                : copy.publicRegistration.linkLoadErrorTitle)}
            />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual
              {...(isNotFound
                ? copy.publicRegistration.invalidLinkDesc
                : copy.publicRegistration.linkLoadErrorDesc)}
            />
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {!isNotFound && (
              <Button onClick={() => void refetch()} className="min-h-11 px-5 text-sm">
                <Bilingual {...copy.publicRegistration.retryLink} />
              </Button>
            )}
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-bold text-on-primary no-underline transition-colors duration-150 hover:bg-primary-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
            >
              <Bilingual {...copy.publicRegistration.loginAfterVerification} />
            </Link>
          </div>
        </section>
      </main>
    );
  }

  if (!isAvailable && !submission) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <Clock3 className="mx-auto size-10 text-warning" aria-hidden="true" />
          <p className="mt-5 font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
            {program.code}
          </p>
          <h1 className="mt-3 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface">
            <Bilingual {...copy.publicRegistration.unavailableTitle} />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual {...copy.publicRegistration.unavailableDesc} />
          </p>
        </section>
      </main>
    );
  }

  if (submission) {
    const isAutoApproved = submission.status === 'APPROVED';
    const whatsappInvitation = submission.whatsappInvitation;
    const expectedGroupGender = submission.gender === 'MALE' ? 'IKHWAN' : 'AKHWAT';
    const isMatchingGender =
      whatsappInvitation?.gender === expectedGroupGender || whatsappInvitation?.gender === 'MIXED';
    const readyWhatsAppInvitation =
      isAutoApproved && isMatchingGender && whatsappInvitation?.inviteLink?.trim()
        ? whatsappInvitation
        : null;
    const supportUrl = `${BID_SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(
      `Assalamu'alaikum, saya membutuhkan bantuan terkait pendaftaran program ${program.name}.`
    )}`;
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface px-6 py-12">
        <section className="w-full max-w-xl rounded-3xl bg-surface-container-low p-8 text-center shadow-level2 sm:p-10">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-success-container text-success">
            <CheckCircle2 className="size-8" aria-hidden="true" />
          </div>
          <p className="mt-5 font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
            {program.name}
          </p>
          <h1
            ref={completionHeading}
            tabIndex={-1}
            className="mt-3 overflow-wrap-anywhere font-headline text-2xl font-bold text-on-surface focus:outline-none"
          >
            <Bilingual
              {...(isAutoApproved
                ? copy.publicRegistration.successAutoTitle
                : copy.publicRegistration.successManualTitle)}
            />
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-on-surface-variant">
            <Bilingual
              {...(isAutoApproved
                ? copy.publicRegistration.successAutoDesc
                : copy.publicRegistration.successManualDesc)}
            />
          </p>
          {submission.participantNip && (
            <div className="mx-auto mt-5 flex max-w-md items-center gap-3 rounded-2xl bg-success-container p-4 text-start text-on-success-container">
              <Fingerprint className="size-5 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-xs font-label font-bold uppercase tracking-wider">
                  <Bilingual {...copy.publicRegistration.generatedNipLabel} />
                </p>
                <p className="mt-1 font-mono text-lg font-extrabold tracking-wider">
                  {submission.participantNip}
                </p>
                <p className="mt-1 text-xs leading-5 opacity-80">
                  <Bilingual {...copy.publicRegistration.generatedNipHelp} />
                </p>
              </div>
            </div>
          )}
          <section className="mx-auto mt-6 w-full max-w-md rounded-2xl bg-secondary-container p-5 text-start">
            {readyWhatsAppInvitation ? (
              <div className="flex items-start gap-3">
                <MessageCircle className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="font-label text-sm font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.joinGroupNowTitle} />
                  </p>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual {...copy.publicRegistration.joinGroupNowDesc} />
                  </p>
                  <p className="mt-2 text-xs font-bold text-primary">
                    <Bilingual
                      {...(readyWhatsAppInvitation.gender === 'IKHWAN'
                        ? copy.publicRegistration.ikhwanGroup
                        : readyWhatsAppInvitation.gender === 'AKHWAT'
                          ? copy.publicRegistration.akhwatGroup
                          : copy.publicRegistration.mixedGroup)}
                    />
                  </p>
                  <p className="mt-1 break-words text-sm font-semibold text-on-surface">
                    {readyWhatsAppInvitation.name}
                  </p>
                  <a
                    href={readyWhatsAppInvitation.inviteLink?.trim()}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary to-primary-container px-4 py-3 text-sm font-bold text-on-primary no-underline transition-colors duration-150 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
                  >
                    <MessageCircle size={16} aria-hidden="true" />
                    <Bilingual {...copy.registration.joinWhatsApp} />
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Clock3 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                <div>
                  <p className="font-label text-sm font-bold text-on-surface">
                    <Bilingual
                      {...(isAutoApproved
                        ? copy.publicRegistration.groupLinkPreparingTitle
                        : copy.publicRegistration.pendingReviewTitle)}
                    />
                  </p>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual
                      {...(isAutoApproved
                        ? copy.publicRegistration.groupLinkPreparingDesc
                        : copy.publicRegistration.pendingReviewDesc)}
                    />
                  </p>
                </div>
              </div>
            )}
            <p className="mt-5 text-sm leading-6 text-on-surface-variant">
              <Bilingual {...copy.publicRegistration.supportHelp} />
            </p>
            <a
              href={supportUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl bg-surface-container-lowest px-4 py-3 text-xs font-label font-bold uppercase tracking-widest text-primary no-underline shadow-level1 transition-colors hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
            >
              <MessageCircle size={16} aria-hidden="true" />
              <Bilingual {...copy.publicRegistration.contactBidSupport} />
            </a>
          </section>
          <Link
            to="/login"
            className="mt-7 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-surface-container-highest px-5 text-sm font-bold text-primary no-underline transition-colors duration-150 hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
          >
            <Bilingual {...copy.publicRegistration.loginAfterVerification} />
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-surface px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-6xl">
        <header className="rounded-[2rem] bg-surface-container-low p-6 shadow-level1 sm:p-9">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end">
            <div className="min-w-0">
              <p className="font-label text-xs font-bold uppercase tracking-[0.14em] text-primary">
                <Bilingual {...copy.publicRegistration.eyebrow} />
              </p>
              <h1 className="mt-3 overflow-wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
                <Bilingual {...copy.publicRegistration.titlePrefix} />
                <span className="block text-primary">{program.name}</span>
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-on-surface-variant sm:text-base">
                {program.description || <Bilingual {...copy.publicRegistration.subtitle} />}
              </p>
            </div>
            {program.coverImage ? (
              <img
                src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                alt={program.name}
                width={576}
                height={324}
                className="aspect-[16/9] w-full rounded-2xl object-cover shadow-level2"
              />
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <UsersRound className="size-10" aria-hidden="true" />
              </div>
            )}
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <form onSubmit={handleSubmit(submit)} className="space-y-6">
            <section className="rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                  <UserRound size={20} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.accountSection} />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual {...copy.publicRegistration.subtitle} />
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Input
                  label={t(copy.publicRegistration.nameLabel)}
                  aria-label={t(copy.publicRegistration.nameLabel)}
                  autoComplete="name"
                  error={fieldError('name')}
                  leftIcon={<UserRound size={17} aria-hidden="true" />}
                  {...register('name')}
                />
                <Input
                  label={t(copy.publicRegistration.emailLabel)}
                  aria-label={t(copy.publicRegistration.emailLabel)}
                  type="email"
                  autoComplete="email"
                  error={fieldError('email')}
                  leftIcon={<Mail size={17} aria-hidden="true" />}
                  {...register('email')}
                />
                <div>
                  <Input
                    label={t(copy.publicRegistration.phoneLabel)}
                    aria-label={t(copy.publicRegistration.phoneLabel)}
                    placeholder={t(copy.publicRegistration.phonePlaceholder)}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    error={fieldError('phone')}
                    leftIcon={<Phone size={17} aria-hidden="true" />}
                    {...register('phone')}
                  />
                  <p className="mt-2 px-1 text-xs leading-5 text-on-surface-variant">
                    {t(copy.publicRegistration.phoneHelp)}
                  </p>
                </div>
                <div>
                  <Input
                    label={t(copy.publicRegistration.dateOfBirthLabel)}
                    aria-label={t(copy.publicRegistration.dateOfBirthLabel)}
                    type="date"
                    autoComplete="bday"
                    max={new Date().toISOString().slice(0, 10)}
                    error={fieldError('dateOfBirth')}
                    leftIcon={<CalendarDays size={17} aria-hidden="true" />}
                    {...register('dateOfBirth')}
                  />
                  {age !== null && (
                    <p className="mt-2 px-1 text-xs font-semibold text-primary" role="status">
                      {t(copy.publicRegistration.ageSummary).replace('{age}', String(age))}
                    </p>
                  )}
                </div>
                <fieldset className="min-w-0">
                  <legend className="mb-2 block text-sm font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.genderLabel} />
                  </legend>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'MALE' as const, label: copy.publicRegistration.male },
                      { value: 'FEMALE' as const, label: copy.publicRegistration.female },
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex min-h-11 cursor-pointer items-center justify-center rounded-xl px-3 text-sm font-semibold transition-colors duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-primary/20 ${
                          selectedGender === option.value
                            ? 'bg-primary text-on-primary'
                            : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                        }`}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          className="sr-only"
                          {...register('gender')}
                        />
                        <Bilingual {...option.label} />
                      </label>
                    ))}
                  </div>
                  {fieldError('gender') && (
                    <p className="mt-2 text-xs text-error">{fieldError('gender')}</p>
                  )}
                </fieldset>
                <div>
                  <label
                    htmlFor="public-registration-occupation"
                    className="mb-2 block text-sm font-bold text-on-surface"
                  >
                    <Bilingual {...copy.participantBackground.occupationLabel} />
                  </label>
                  <select
                    id="public-registration-occupation"
                    autoComplete="organization-title"
                    aria-describedby="public-registration-occupation-help"
                    aria-invalid={Boolean(fieldError('occupation'))}
                    className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
                    {...register('occupation')}
                  >
                    <option value="">{t(copy.participantBackground.chooseOccupation)}</option>
                    {PARTICIPANT_OCCUPATIONS.map(option => (
                      <option key={option} value={option}>
                        {t(copy.participantBackground.options[option])}
                      </option>
                    ))}
                  </select>
                  <p
                    id="public-registration-occupation-help"
                    className="mt-2 text-xs leading-5 text-on-surface-variant"
                  >
                    <Bilingual {...copy.participantBackground.occupationHelp} />
                  </p>
                  {fieldError('occupation') && (
                    <p className="mt-1 text-xs text-error">{fieldError('occupation')}</p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="public-registration-education"
                    className="mb-2 block text-sm font-bold text-on-surface"
                  >
                    <Bilingual {...copy.participantBackground.educationLabel} />
                  </label>
                  <select
                    id="public-registration-education"
                    autoComplete="education-level"
                    aria-describedby="public-registration-education-help"
                    aria-invalid={Boolean(fieldError('educationLevel'))}
                    className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
                    {...register('educationLevel')}
                  >
                    <option value="">{t(copy.participantBackground.chooseEducation)}</option>
                    {PARTICIPANT_EDUCATION_LEVELS.map(option => (
                      <option key={option} value={option}>
                        {t(copy.participantBackground.options[option])}
                      </option>
                    ))}
                  </select>
                  <p
                    id="public-registration-education-help"
                    className="mt-2 text-xs leading-5 text-on-surface-variant"
                  >
                    <Bilingual {...copy.participantBackground.educationHelp} />
                  </p>
                  {fieldError('educationLevel') && (
                    <p className="mt-1 text-xs text-error">{fieldError('educationLevel')}</p>
                  )}
                </div>
                {occupation === 'OTHER' && (
                  <Input
                    label={t(copy.participantBackground.occupationOtherLabel)}
                    aria-label={t(copy.participantBackground.occupationOtherLabel)}
                    placeholder={t(copy.participantBackground.occupationOtherPlaceholder)}
                    autoComplete="organization-title"
                    error={fieldError('occupationOther')}
                    {...register('occupationOther')}
                  />
                )}
                {educationLevel === 'OTHER' && (
                  <Input
                    label={t(copy.participantBackground.educationOtherLabel)}
                    aria-label={t(copy.participantBackground.educationOtherLabel)}
                    placeholder={t(copy.participantBackground.educationOtherPlaceholder)}
                    error={fieldError('educationOther')}
                    {...register('educationOther')}
                  />
                )}
                <Input
                  label={t(copy.publicRegistration.passwordLabel)}
                  aria-label={t(copy.publicRegistration.passwordLabel)}
                  type="password"
                  autoComplete="new-password"
                  error={fieldError('password')}
                  leftIcon={<LockKeyhole size={17} aria-hidden="true" />}
                  {...register('password')}
                />
                <Input
                  label={t(copy.publicRegistration.confirmPasswordLabel)}
                  aria-label={t(copy.publicRegistration.confirmPasswordLabel)}
                  type="password"
                  autoComplete="new-password"
                  error={fieldError('confirmPassword')}
                  leftIcon={<LockKeyhole size={17} aria-hidden="true" />}
                  {...register('confirmPassword')}
                />
              </div>
              <p className="mt-3 text-xs leading-5 text-on-surface-variant">
                {t(copy.publicRegistration.passwordHelp)}
                {password ? ` (${password.length}/8)` : ''}
              </p>
            </section>

            <section className="rounded-3xl bg-surface-container-lowest p-5 shadow-level1 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-secondary-container p-2.5 text-on-secondary-container">
                  <MapPin size={20} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.publicRegistration.profileSection} />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">
                    <Bilingual {...copy.registration.verifyIdentityDesc} />
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-5">
                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-on-surface"
                    htmlFor="public-registration-city"
                  >
                    <Bilingual {...copy.publicRegistration.cityLabel} />
                  </label>
                  <CityAutocomplete
                    id="public-registration-city"
                    value={domisili}
                    onChange={value => setValue('domisili', value, { shouldValidate: true })}
                    placeholder={t(copy.registration.cityPlaceholder)}
                    dropdownLabel={t(copy.registration.cityDropdownLabel)}
                    emptyMessage={query =>
                      t(copy.registration.cityNoResults).replace('{query}', query)
                    }
                    error={fieldError('domisili')}
                    errorId="public-registration-city-error"
                  />
                  {fieldError('domisili') && (
                    <p
                      id="public-registration-city-error"
                      role="alert"
                      className="mt-2 text-xs text-error"
                    >
                      {fieldError('domisili')}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    className="mb-2 block text-sm font-bold text-on-surface"
                    htmlFor="public-registration-reason"
                  >
                    <Bilingual {...copy.publicRegistration.reasonLabel} />
                  </label>
                  <textarea
                    id="public-registration-reason"
                    rows={5}
                    className="min-h-28 w-full rounded-2xl bg-surface-container-low px-4 py-3 text-sm leading-6 text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 focus:ring-2 focus:ring-primary/20"
                    placeholder={t(copy.publicRegistration.reasonPlaceholder)}
                    aria-invalid={Boolean(fieldError('reason'))}
                    aria-describedby={
                      fieldError('reason') ? 'public-registration-reason-error' : undefined
                    }
                    {...register('reason')}
                  />
                  {fieldError('reason') && (
                    <p
                      id="public-registration-reason-error"
                      role="alert"
                      className="mt-2 text-xs text-error"
                    >
                      {fieldError('reason')}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-3xl bg-surface-container-low p-5 shadow-level1 sm:p-7">
              {submitError && (
                <div
                  role="alert"
                  className="mb-5 rounded-2xl bg-error-container p-4 text-sm text-on-error-container"
                >
                  {submitError}
                  <a
                    href={BID_SUPPORT_WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg bg-surface-container-lowest px-4 py-2 font-semibold text-primary no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <MessageCircle size={16} aria-hidden="true" />
                    <Bilingual {...copy.publicRegistration.contactBidSupport} />
                  </a>
                </div>
              )}
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-surface-container-lowest p-4 focus-within:outline focus-within:outline-2 focus-within:outline-primary/20">
                <input
                  type="checkbox"
                  className="mt-0.5 size-5 shrink-0 accent-primary"
                  aria-invalid={Boolean(fieldError('consent'))}
                  {...register('consent')}
                />
                <span className="text-sm leading-6 text-on-surface">
                  <Bilingual {...copy.publicRegistration.consent} />
                </span>
              </label>
              {fieldError('consent') && (
                <p className="mt-2 text-xs text-error">{fieldError('consent')}</p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isSubmitting}
                className="mt-5 min-h-12 w-full justify-center gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin" size={18} aria-hidden="true" />
                ) : (
                  <ArrowRight size={18} aria-hidden="true" />
                )}
                <Bilingual
                  {...(isSubmitting
                    ? copy.publicRegistration.submitting
                    : copy.publicRegistration.submit)}
                />
              </Button>
            </section>
          </form>

          <aside className="rounded-3xl bg-surface-container-low p-5 shadow-level1 lg:sticky lg:top-6">
            <div className="flex items-center gap-2 text-primary">
              <ShieldCheck size={18} aria-hidden="true" />
              <h2 className="font-headline text-base font-bold text-on-surface">
                <Bilingual {...copy.publicRegistration.programInfo} />
              </h2>
            </div>
            <dl className="mt-5 space-y-3">
              <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  {program.code}
                </dt>
                <dd className="mt-1 overflow-wrap-anywhere text-sm font-semibold text-on-surface">
                  {program.name}
                </dd>
              </div>
              <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                <dt className="flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  <CalendarDays size={13} aria-hidden="true" />
                  <Bilingual {...copy.publicRegistration.scheduleLabel} />
                </dt>
                <dd className="mt-1 text-sm font-semibold text-on-surface">{registrationWindow}</dd>
              </div>
              {program.startDate && (
                <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                  <dt className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.publicRegistration.startDateLabel} />
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-on-surface">
                    {new Date(program.startDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </dd>
                </div>
              )}
              <div className="rounded-2xl bg-surface-container-lowest p-3.5">
                <dt className="flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                  <Fingerprint size={13} aria-hidden="true" />
                  <Bilingual {...copy.publicRegistration.nipSettingLabel} />
                </dt>
                <dd className="mt-1 text-sm font-semibold text-on-surface">
                  <Bilingual
                    {...(program.generateParticipantNip
                      ? copy.publicRegistration.nipGeneratedAutomatically
                      : copy.publicRegistration.nipNotGenerated)}
                  />
                </dd>
                <dd className="mt-1 text-xs leading-5 text-on-surface-variant">
                  <Bilingual {...copy.publicRegistration.nipFormatHelp} />
                </dd>
              </div>
            </dl>
            <div
              className={`mt-5 rounded-2xl p-4 text-sm leading-6 ${program.approvalMode === 'AUTO_APPROVE' ? 'bg-success-container text-on-success-container' : 'bg-warning-container text-on-warning-container'}`}
            >
              <p className="font-bold">
                <Bilingual
                  {...(program.approvalMode === 'AUTO_APPROVE'
                    ? copy.publicRegistration.directEnrollment
                    : copy.publicRegistration.manualReview)}
                />
              </p>
              <p className="mt-1 opacity-80">
                <Bilingual
                  {...(program.approvalMode === 'AUTO_APPROVE'
                    ? copy.publicRegistration.autoEnrollmentHelp
                    : copy.publicRegistration.manualReviewHelp)}
                />
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
